import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from './components/Navbar';
import Footer from '../home/components/Footer';
import SeoHead from '../../components/feature/SeoHead';

interface GalleryCategory {
  id: string;
  title: string;
  subtitle: string;
  coverImage: string;
  thumbnails: string[];
}

const categories: GalleryCategory[] = [
  {
    id: 'classical',
    title: 'Classical World',
    subtitle: '',
    coverImage: 'https://static.readdy.ai/image/37826dbbd11b57a8921406d974f7aedc/80ee417ff0256540675e72188d35ac63.jpeg',
    thumbnails: [],
  },
  {
    id: 'ventures',
    title: 'Ventures beyond Classical',
    subtitle: '',
    coverImage: 'https://static.readdy.ai/image/37826dbbd11b57a8921406d974f7aedc/a6c57bb53b82932ca69df591a505e0ee.jpeg',
    thumbnails: [],
  },
];

interface CategoryCardProps {
  category: GalleryCategory;
  index: number;
}

function CategoryCard({ category, index }: CategoryCardProps) {
  const [hovered, setHovered] = useState(false);
  const navigate = useNavigate();

  const isVentures = category.id === 'ventures';

  // 墨绿色系
  const greenAccent = '#3a7a52';
  const greenAccentBright = '#4e9e6a';
  const greenBorder = 'rgba(58,122,82,0.5)';
  const greenBorderDim = 'rgba(58,122,82,0.25)';
  const greenGlow = 'rgba(58,122,82,0.3)';
  const greenGlowSoft = 'rgba(58,122,82,0.12)';

  // 金色系
  const goldAccent = '#c9a84c';
  const goldAccentBright = '#f0d060';
  const goldBorder = 'rgba(201,168,76,0.5)';
  const goldBorderDim = 'rgba(201,168,76,0.25)';
  const goldGlow = 'rgba(201,168,76,0.3)';
  const goldGlowSoft = 'rgba(201,168,76,0.12)';

  const accent = isVentures ? greenAccent : goldAccent;
  const accentBright = isVentures ? greenAccentBright : goldAccentBright;
  const borderColor = isVentures ? greenBorder : goldBorder;
  const borderDim = isVentures ? greenBorderDim : goldBorderDim;
  const glowColor = isVentures ? greenGlow : goldGlow;
  const glowSoft = isVentures ? greenGlowSoft : goldGlowSoft;
  const overlayColor = isVentures ? 'rgba(58,122,82,0.04)' : 'rgba(201,168,76,0.04)';
  const overlayColorBottom = isVentures ? 'rgba(58,122,82,0.06)' : 'rgba(201,168,76,0.06)';

  const handleClick = () => {
    navigate(`/gallery/${category.id}`);
  };

  return (
    <div
      className="relative overflow-hidden cursor-pointer group"
      style={{
        animationDelay: `${index * 150}ms`,
        borderRadius: '4px',
        transition: 'box-shadow 0.6s ease',
        boxShadow: hovered
          ? `0 0 18px 4px ${glowColor}, 0 0 36px 8px ${glowSoft}, inset 0 0 0 1px ${borderColor}`
          : `0 0 0px 0px rgba(0,0,0,0), inset 0 0 0 1px ${borderDim}`,
        aspectRatio: '3 / 4',
        width: '100%',
      }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onClick={handleClick}
    >
      {/* Cover Image */}
      <img
        src={category.coverImage}
        alt={category.title}
        className="absolute inset-0 w-full h-full object-cover object-center transition-transform duration-700 group-hover:scale-105"
      />

      {/* Dark overlay */}
      <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/30 to-black/10 transition-opacity duration-500" />

      {/* Accent glow border overlay */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          transition: 'opacity 0.6s ease',
          opacity: hovered ? 1 : 0,
          background: `linear-gradient(to bottom, ${overlayColor} 0%, transparent 30%, transparent 70%, ${overlayColorBottom} 100%)`,
        }}
      />

      {/* Bottom title area */}
      <div className="absolute bottom-0 left-0 right-0 px-8 pb-10 pt-24 bg-gradient-to-t from-black/90 via-black/60 to-transparent">
        {/* Decorative line */}
        <div
          className="mb-5 transition-all duration-500"
          style={{
            height: '1px',
            background: `linear-gradient(to right, ${accent}e6, ${accent}4d, transparent)`,
            width: hovered ? '85%' : '40%',
          }}
        />

        <h2
          className="font-cinzel-decorative font-bold mb-2 transition-all duration-500"
          style={{
            fontSize: 'clamp(1.8rem, 3vw, 2.6rem)',
            color: hovered ? accentBright : accent,
            letterSpacing: '0.06em',
            textShadow: hovered
              ? `0 0 24px ${accentBright}b3, 0 2px 12px rgba(0,0,0,0.8)`
              : '0 2px 12px rgba(0,0,0,0.8)',
            transition: 'color 0.4s ease, text-shadow 0.4s ease',
          }}
        >
          {category.title}
        </h2>

        {/* View label */}
        <div
          className="flex items-center gap-2 mt-4 transition-all duration-500"
          style={{
            opacity: hovered ? 1 : 0,
            transform: hovered ? 'translateY(0)' : 'translateY(8px)',
          }}
        >
          <i className="ri-image-line text-sm" style={{ color: accent }}></i>
          <span
            className="font-cinzel text-xs tracking-widest"
            style={{ color: accent }}
          >
            VIEW GALLERY
          </span>
        </div>
      </div>
    </div>
  );
}

export default function GalleryPage() {
  const galleryJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: 'Wei Chen – Photo Gallery',
    description: 'Photo gallery of baritone Wei Chen. Classical opera performances and ventures beyond classical music.',
    url: `${import.meta.env.VITE_SITE_URL ?? 'https://example.com'}/gallery`,
    author: {
      '@type': 'Person',
      name: 'Wei Chen',
      jobTitle: 'Baritone',
    },
  };

  return (
    <div className="min-h-screen bg-deep-black">
      <SeoHead
        title="Wei Chen – Photo Gallery | Opera & Performance Photography"
        description="Browse the photo gallery of New York baritone Wei Chen. Classical opera performances, concert moments, and ventures beyond classical music."
        keywords="Wei Chen Photos, Opera Photography, Baritone Gallery, Classical Performance, New York Opera"
        canonical="/gallery"
        jsonLd={galleryJsonLd}
      />
      <Navbar />

      {/* Hero Header */}
      <section className="relative pt-32 pb-10 px-6 border-b border-champagne-gold/20 overflow-hidden">
        {/* Marble Checkerboard Background */}
        <div className="absolute inset-0 z-0">
          <img
            src="https://public.readdy.ai/ai/img_res/edited_52313795a64d92c0d526109107e099c3_ed141df2.jpg"
            alt="marble background"
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/50 to-black/65"></div>
          <div
            className="absolute inset-0 pointer-events-none"
            style={{
              background: 'linear-gradient(to bottom, rgba(0,0,0,0.35) 0%, transparent 22%, transparent 72%, rgba(0,0,0,0.72) 100%)',
            }}
          ></div>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto text-center">
          <h1 className="text-5xl md:text-6xl font-cinzel-decorative font-bold mb-4" style={{ color: '#c9a84c', textShadow: '0 2px 12px rgba(0,0,0,0.8)' }}>
            Photo Gallery
          </h1>
          <div className="flex items-center justify-center gap-4 mb-4">
            <div className="h-px w-16 bg-champagne-gold/40"></div>
            <div className="w-5 h-5 flex items-center justify-center" style={{ color: '#c9a84c', opacity: 0.6 }}>
              <i className="ri-camera-line text-sm"></i>
            </div>
            <div className="h-px w-16 bg-champagne-gold/40"></div>
          </div>
          <p className="font-cormorant italic text-lg md:text-xl mb-2" style={{ color: '#ede8d8', opacity: 0.75, textShadow: '0 1px 6px rgba(0,0,0,0.7)' }}>
            Moments On and Off the Stage
          </p>
        </div>
      </section>

      {/* Spacer between hero and category grid */}
      <div className="h-10 bg-[#111111]" />

      {/* Two-column Category Modules */}
      <section className="pb-24 px-4 md:px-6 bg-[#111111]">
        <div className="max-w-4xl mx-auto">
          <div className="flex flex-col md:flex-row gap-4 md:gap-8">
            {categories.map((cat, i) => (
              <CategoryCard key={cat.id} category={cat} index={i} />
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}